import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import CommonPage from "@icarius-common/commonPage";
import Loader from "@icarius-common/loader";
import AvatarRenderer from "@icarius-table/renderersAndFilters/avatarRenderer";
import AbsenceRenderer from "@icarius-table/renderersAndFilters/absenceRenderer";
import { createAggridColumn } from "@icarius-table/utils";
import { createDateFromYYYYMMDD } from "@icarius-utils/date";
import { formatFirstLetterUppercase } from "@icarius-utils/format";
import { getAbsentCollaboratorsAction } from "../actions";
import { getData, getIsLoading } from "../selectors";
import { RESET_STATE } from "../actionTypes";
import { language, getLocalizedString } from "@icarius-localization/strings";

const AbsentCollaboratorsDialog = ({ open, handleClose }) => {

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAbsentCollaboratorsAction());

    return () => {
      dispatch({ type: RESET_STATE });
    }
  }, [dispatch])

  const isLoading = useSelector(getIsLoading);
  const data = useSelector(getData);

  const ownColumnDef = useMemo(() => {
    let columnDefinitions = [
      createAggridColumn("image", "AvatarRenderer", getLocalizedString("photo")),
      createAggridColumn("name", "Alfa", getLocalizedString("nameAndSurname")),
    ];

    if (data?.length > 0) {
      data[0].days.forEach((day, index) => {
        let dateObject = createDateFromYYYYMMDD(day.date);
        const dateWeekday = dateObject.toLocaleDateString(language, { weekday: "long", });
        const dateNumber = dateObject.toLocaleDateString(language, { day: "numeric" });
        const dateMonth = dateObject.toLocaleDateString(language, { month: "long", });
        let dateName = `${index === 0 ? "Hoy, " : ""}${formatFirstLetterUppercase(dateWeekday)} ${dateNumber} de ${formatFirstLetterUppercase(dateMonth)}`;

        if (language === "en") {
          let ordinal = "th";

          switch (dateNumber) {
            case 1:
            case 31:
              ordinal = "st";
              break;
            case 2:
              ordinal = "nd";
              break;
            case 3:
              ordinal = "rd";
              break;
            default:
              ordinal = "th";
              break;
          }
          dateName = `${index === 0 ? "Today, " : ""}${formatFirstLetterUppercase(dateWeekday)} ${dateNumber}${ordinal} ${formatFirstLetterUppercase(dateMonth)} `;
        }

        columnDefinitions.push(createAggridColumn(day.date, "AbsenceRenderer", dateName));
      });
    }

    columnDefinitions.push({
      ...createAggridColumn("comment", "Alfa", getLocalizedString("comments")),
      width: 500,
    });

    return columnDefinitions;
  }, [data])

  const formattedData = useMemo(() => {
    return data.map((item) => {
      const aux = { ...item };

      item.days.forEach((day) => {
        aux[day.date] = day.type;
      });

      return aux;
    })
  }, [data])

  if (isLoading) return <Loader open={isLoading} />

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"lg"}
      fullWidth
    >
      <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {getLocalizedString("absentCollaborators")}
          <DialogTitleDivider />
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0 }}>
          <CommonPage
            gridTitle={getLocalizedString("absentCollaborators")}
            ownColumnDef={ownColumnDef}
            rowData={formattedData}
            gridHeight={'70vh'}
            ignoreProcessesbyPath
            frameworkComponents={{ AvatarRenderer, AbsenceRenderer }}
          />
        </DialogContent>
      </div>
    </Dialog>
  );
}

export default AbsentCollaboratorsDialog;